import { A, useNavigate } from "@solidjs/router";
import { createSignal, For, Show } from "solid-js";
import { adminGateProps } from "../../store/admin";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import { FormError } from "../../ui/FormError";
import { relativeAge } from "../../ui/format";
import { createPlaygroundWorkspaces } from "../playground/playgroundQueries";
import { createEvalSuites, invalidateEvalSuites } from "./evalQueries";

/**
 * Suite index: the named sets of pages a batch runs over. Membership is fixed
 * at creation — the backend refuses to edit one — so the picker is part of the
 * create form and never appears again.
 */
export function EvalSuiteList() {
  const navigate = useNavigate();
  const suites = createEvalSuites();
  const workspaces = createPlaygroundWorkspaces();
  const [name, setName] = createSignal("");
  const [picked, setPicked] = createSignal<readonly string[]>([]);
  const action = createAsyncAction();

  const toggle = (websiteId: string) =>
    setPicked((current) =>
      current.includes(websiteId)
        ? current.filter((id) => id !== websiteId)
        : [...current, websiteId],
    );

  const create = (event: Event) => {
    event.preventDefault();
    if (!name().trim() || picked().length === 0 || action.busy()) return;
    void action.run(async () => {
      const suite = await api.evals.createSuite({
        name: name().trim(),
        websiteIds: [...picked()],
      });
      await invalidateEvalSuites();
      navigate(`/evals/suites/${suite.suiteId}`);
    });
  };

  return (
    <main class="landing">
      <div class="landing-bar">
        <span class="landing-brand">Faro</span>
        <span class="landing-tag">Evals</span>
        <A href="/playground" class="btn">
          Playground
        </A>
        <A href="/" class="btn">
          ← All websites
        </A>
      </div>
      <div class="landing-main">
        <div class="landing-head-row">
          <div>
            <h1 class="landing-h1">Evaluation suites</h1>
            <p class="landing-sub pg-lede">
              A suite is a fixed set of pages. Run a batch over one to put two
              agent configurations against each other on the same evidence, page
              by page, and see which wins.
            </p>
          </div>
        </div>
        <FormError message={action.error()} />

        <section class="panel">
          <div class="panel-head">
            <span class="panel-title">New suite</span>
          </div>
          <div class="panel-body">
            <form onSubmit={create}>
              <input
                class="editor-input"
                placeholder="Suite name"
                value={name()}
                onInput={(event) => setName(event.currentTarget.value)}
              />
              <p class="landing-sub">
                Pick the playground pages this suite covers.
              </p>
              <Show
                when={workspaces.data}
                fallback={<div class="loading">Loading pages…</div>}
              >
                {(data) => (
                  <For
                    each={data().workspaces}
                    fallback={
                      <div class="empty">
                        <div class="empty-title">No playground pages yet</div>
                        <div class="empty-hint">
                          Add pages in the playground first — a suite runs over
                          them.
                        </div>
                      </div>
                    }
                  >
                    {(workspace) => (
                      <div class="panel-body">
                        <div class="panel-title">{workspace.name}</div>
                        <For each={workspace.websites}>
                          {(site) => (
                            <label class="pg-card">
                              <input
                                type="checkbox"
                                checked={picked().includes(site.websiteId)}
                                onChange={() => toggle(site.websiteId)}
                              />
                              <span class="mono">{site.originUrl}</span>
                            </label>
                          )}
                        </For>
                      </div>
                    )}
                  </For>
                )}
              </Show>
              <button
                class="btn btn-primary"
                type="submit"
                {...adminGateProps(
                  action.busy() || !name().trim() || picked().length === 0,
                  "create",
                )}
              >
                Create suite ({picked().length} pages)
              </button>
            </form>
          </div>
        </section>

        <div class="panel">
          <Show
            when={suites.data}
            fallback={<div class="loading">Loading suites…</div>}
          >
            {(data) => (
              <Show
                when={data().length > 0}
                fallback={
                  <div class="empty">
                    <div class="empty-title">No suites yet</div>
                  </div>
                }
              >
                <table class="tbl tbl-link">
                  <thead>
                    <tr>
                      <th>Suite</th>
                      <th>Pages</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={data()}>
                      {(suite) => (
                        <tr
                          class="tbl-row"
                          onClick={() =>
                            navigate(`/evals/suites/${suite.suiteId}`)
                          }
                        >
                          <td>{suite.name}</td>
                          <td>{suite.websiteCount}</td>
                          <td>{relativeAge(suite.createdAt)}</td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </Show>
            )}
          </Show>
        </div>
      </div>
    </main>
  );
}
